import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { FeatureCollection, Geometry } from "geojson";
import { RootState } from "@/app/rootReducer";
import {
  fetchZipcodeMarketValuesAction,
  setSelectedTrade,
  setSelectedColorMap,
  clearMarketData,
  ColorMapType,
} from "@/modules/stochastic/features/TotalAddressableMarket/slices/totalAddressableMarketSlice";
import {
  TradeType,
  getMarketValueKey,
} from "@/modules/stochastic/features/TotalAddressableMarket/api/fetchZipcodeMarketValues/types";
import { useZipCodeInput } from "./useZipCodeInput";
import { fetchLocations } from "@/modules/stochastic/features/LocationsList/api/fetchLocations/fetchLocationsApi";
import { Location } from "@/modules/stochastic/features/LocationsList/api/createLocation/types";
import {
  BaseEntity,
  FetchEntitiesFn,
} from "@/components/EntityPickerModal/EntityPickerModal";

interface ZipcodeFeatureProperties {
  zipcode: string;
  market_value_electrical: number | null;
  market_value_plumbing: number | null;
  market_value_hvac: number | null;
  market_value_roofing: number | null;
}

// Location type that extends BaseEntity for the EntityPickerModal
interface LocationEntity extends BaseEntity {
  id: number;
  name: string;
  postalCodes: string[];
}

// Adapter function to fetch locations compatible with EntityPickerModal
const fetchLocationsForPicker: FetchEntitiesFn<LocationEntity> = async ({
  searchTerm,
  page,
  pageSize,
}) => {
  const response = await fetchLocations({
    searchTerm: searchTerm || undefined,
    page,
    perPage: pageSize,
    isActive: 1,
  });

  return {
    data: response.data.map((loc: Location) => ({
      id: loc.id,
      name: loc.name,
      postalCodes: loc.postalCodes,
    })),
    totalCount: response.meta.totalCount || 0,
  };
};

export function useTotalAddressableMarket() {
  const dispatch = useDispatch();

  const {
    marketData,
    selectedTrade,
    selectedColorMap,
    loading,
    error,
    dataVersion,
  } = useSelector((state: RootState) => state.totalAddressableMarket);

  const zipCodeInputHook = useZipCodeInput();
  const [selectedLocations, setSelectedLocations] = useState<LocationEntity[]>(
    [],
  );

  const handleTradeChange = useCallback(
    (trade: TradeType) => {
      dispatch(setSelectedTrade(trade));
    },
    [dispatch],
  );

  const handleColorMapChange = useCallback(
    (colorMap: ColorMapType) => {
      dispatch(setSelectedColorMap(colorMap));
    },
    [dispatch],
  );

  const handleLocationChange = useCallback(
    (locations: LocationEntity[]) => {
      setSelectedLocations(locations);

      const postalCodes = locations.flatMap((loc) => loc.postalCodes);
      if (postalCodes.length > 0) {
        zipCodeInputHook.appendZipCodes(postalCodes);
      }
    },
    [zipCodeInputHook],
  );

  const handleSubmit = useCallback(() => {
    if (zipCodeInputHook.zipCodeList.length === 0) {
      return;
    }
    dispatch(fetchZipcodeMarketValuesAction(zipCodeInputHook.zipCodeList));
  }, [dispatch, zipCodeInputHook.zipCodeList]);

  const handleClear = useCallback(() => {
    dispatch(clearMarketData());
    zipCodeInputHook.handleClearAll();
    setSelectedLocations([]);
  }, [dispatch, zipCodeInputHook]);

  const geoJsonData: FeatureCollection<Geometry, ZipcodeFeatureProperties> =
    useMemo(
      () => ({
        type: "FeatureCollection",
        features: marketData
          .filter((item) => item.geometry !== null)
          .map((item) => ({
            type: "Feature" as const,
            properties: {
              zipcode: item.zipcode,
              market_value_electrical: item.market_value_electrical,
              market_value_plumbing: item.market_value_plumbing,
              market_value_hvac: item.market_value_hvac,
              market_value_roofing: item.market_value_roofing,
            },
            geometry: item.geometry as Geometry,
          })),
      }),
      [marketData],
    );

  const marketValueKey = getMarketValueKey(selectedTrade);
  const values = marketData
    .map((item) => item[marketValueKey] as number | null)
    .filter((v): v is number => v !== null);
  const minValue = values.length > 0 ? Math.min(...values) : 0;
  const maxValue = values.length > 0 ? Math.max(...values) : 0;

  return {
    marketData,
    selectedTrade,
    selectedColorMap,
    selectedLocations,
    loading,
    error,
    dataVersion,
    geoJsonData,
    marketValueKey,
    minValue,
    maxValue,
    ...zipCodeInputHook,
    handleTradeChange,
    handleColorMapChange,
    handleSubmit,
    handleClear,
    handleLocationChange,
    fetchLocationsForPicker,
  };
}
